export enum ProjectState {
  SETUP = 'SETUP',
  INGESTION = 'INGESTION',
  TITLE_ABSTRACT_SCREENING = 'TITLE_ABSTRACT_SCREENING',
  FULL_TEXT_RETRIEVAL = 'FULL_TEXT_RETRIEVAL',
  FULL_TEXT_SCREENING = 'FULL_TEXT_SCREENING',
  DATA_EXTRACTION = 'DATA_EXTRACTION',
  COMPLETED = 'COMPLETED',
}

export interface StageMeta {
  state: ProjectState;
  number: string;
  label: string;
  description: string;
  routeSegment: string | null;
}

export const STAGE_META: readonly StageMeta[] = [
  {
    state: ProjectState.SETUP,
    number: '1a',
    label: 'Setup',
    description: 'Configure project settings, add researchers, and define screening criteria.',
    routeSegment: 'setup',
  },
  {
    state: ProjectState.INGESTION,
    number: '1b',
    label: 'Ingestion',
    description: 'Import papers via OpenAlex, BibTeX, PDF uploads, or the group corpus.',
    routeSegment: 'corpus',
  },
  {
    state: ProjectState.TITLE_ABSTRACT_SCREENING,
    number: '2',
    label: 'Title / Abstract Screening',
    description: 'Screen papers by title and abstract, include or exclude with reasons.',
    routeSegment: 'title-abstract-screening',
  },
  {
    state: ProjectState.FULL_TEXT_RETRIEVAL,
    number: '3',
    label: 'Full-Text Retrieval',
    description: 'Retrieve and verify PDF availability for screened papers.',
    routeSegment: 'full-text-retrieval',
  },
  {
    state: ProjectState.FULL_TEXT_SCREENING,
    number: '4',
    label: 'Full-Text Screening',
    description: 'Full-text review with include/exclude decisions and conflict resolution.',
    routeSegment: 'full-text-screening',
  },
  {
    state: ProjectState.DATA_EXTRACTION,
    number: '5',
    label: 'Data Extraction',
    description: 'Extract structured data from included papers using the configured schema.',
    routeSegment: 'data-extraction',
  },
  {
    state: ProjectState.COMPLETED,
    number: '6',
    label: 'Completed',
    description: 'Project is finished — no further actions available.',
    routeSegment: 'completed',
  },
] as const;

export const STAGE_META_BY_STATE: Record<ProjectState, StageMeta> = STAGE_META.reduce(
  (acc, meta) => {
    acc[meta.state] = meta;
    return acc;
  },
  {} as Record<ProjectState, StageMeta>,
);

export function stageMetaFor(state: ProjectState | string): StageMeta | undefined {
  return STAGE_META_BY_STATE[state as ProjectState];
}

export function formatStageState(state?: string): string {
  if (!state) return '';
  const meta = stageMetaFor(state);
  if (meta) return meta.label;
  return state
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function getStageStateClass(state?: string): string {
  if (!state) return 'badge-neutral';
  const map: Record<string, string> = {
    SETUP: 'badge-setup',
    INGESTION: 'badge-ingestion',
    TITLE_ABSTRACT_SCREENING: 'badge-title-abstract-screening',
    FULL_TEXT_RETRIEVAL: 'badge-full-text-retrieval',
    FULL_TEXT_SCREENING: 'badge-full-text-screening',
    DATA_EXTRACTION: 'badge-data-extraction',
    COMPLETED: 'badge-completed',
  };
  return map[state] || 'badge-neutral';
}

export function stageToChildRoute(state: string): string | null {
  const meta = stageMetaFor(state);
  return meta?.routeSegment ?? null;
}

export function stageForChildPath(segments: string[]): ProjectState {
  const first = segments[0] ?? '';
  const routeMap: Record<string, ProjectState> = {
    setup: ProjectState.SETUP,
    corpus: ProjectState.INGESTION,
    'title-abstract-screening': ProjectState.TITLE_ABSTRACT_SCREENING,
    'full-text-retrieval': ProjectState.FULL_TEXT_RETRIEVAL,
    'full-text-screening': ProjectState.FULL_TEXT_SCREENING,
    'conflict-resolution': ProjectState.FULL_TEXT_SCREENING,
    annotator: ProjectState.DATA_EXTRACTION,
    'data-extraction': ProjectState.DATA_EXTRACTION,
    completed: ProjectState.COMPLETED,
  };
  return routeMap[first] ?? ProjectState.SETUP;
}

export function stageIndex(state: ProjectState | string): number {
  const idx = STAGE_META.findIndex((m) => m.state === state);
  return idx === -1 ? 0 : idx;
}

export function isStageReached(
  currentState: ProjectState | string,
  targetState: ProjectState | string,
): boolean {
  return stageIndex(targetState) <= stageIndex(currentState);
}
